import React, { useCallback, useMemo } from 'react';
import { HideOutsideViewport } from '@ui/HideOutsideViewport';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { formatProductRowQuantity } from '@features/products/product-list.helpers';
import { useElementListGroupBarScale } from '@features/proposals/use-element-list-group-bar-scale.hooks';
import { useExpandedItems } from '@client/hooks/use-expand-elements.hooks';
import { getElementCategory } from '@shared/helpers/element-category.helpers';
import { getElementIconCategoryName } from '@features/elements/helpers/get-element-category-icon-name.helper';
import { useSelectedElementId } from '@client/store/ui';
import { useNavigateToElementListSelection } from '@client/hooks/use-router.hooks';
import { Page } from '@client/store/ui/ui.model';
import type { IProductListCategoryGroupView } from '@features/products/product-list.helpers';
import ElementListRow, {
  ELEMENT_LIST_ROW_HEIGHT,
} from '@features/proposals/ElementListRow/ElementListRow';
import ProductGroupItemRow from './ProductGroupItemRow';
import { useProductGroupProposalBars } from './use-product-group-proposal-bars.hooks';

type ProductCategoryGroupRowProps = {
  categoryGroup: IProductListCategoryGroupView;
  hideDivider?: boolean;
  startDepth?: number;
  /** When true, overrides local expanded state (used while searching). */
  forceExpanded?: boolean;
};

const EXPANDED_PREFIX = 'product-category';

const ProductCategoryGroupRow: React.FC<ProductCategoryGroupRowProps> = ({
  categoryGroup,
  hideDivider,
  startDepth = 0,
  forceExpanded,
}) => {
  const {
    productCategoryGroupBarScale: {
      maxCo2: co2ePerGfaMax,
      maxCost: costPerGfaMax,
    },
  } = useElementListGroupBarScale();
  const selectedElementId = useSelectedElementId();
  const navigateToListSelection = useNavigateToElementListSelection();

  const { is: isExpanded, set: setExpanded } = useExpandedItems({
    defaultPrefix: EXPANDED_PREFIX,
  });
  const expanded = forceExpanded ?? isExpanded(categoryGroup.id);

  const categoryIcon = getElementIconCategoryName(categoryGroup.id);
  const categoryName =
    getElementCategory(categoryGroup.id)?.name ?? categoryGroup.id;

  const totalQuantity = getConversionFactorValue(
    categoryGroup.results,
    categoryGroup.unit,
  );
  const { quantityPlaceholder, quantityDisplayNumber, displayUnit } =
    formatProductRowQuantity(totalQuantity, categoryGroup.unit);

  const allItemIds = useMemo(
    () => categoryGroup.elements.flatMap((g) => g.elements.map((i) => i.id)),
    [categoryGroup],
  );
  const proposalBars = useProductGroupProposalBars(allItemIds);

  const handleExpandedChange = useCallback(
    (v: boolean) => setExpanded(categoryGroup.id, v),
    [setExpanded, categoryGroup.id],
  );

  const handleSelect = useCallback(
    () => navigateToListSelection(categoryGroup.id, { page: Page.Products }),
    [navigateToListSelection, categoryGroup.id],
  );

  const selected = selectedElementId === categoryGroup.id;

  return (
    <>
      <HideOutsideViewport
        height={ELEMENT_LIST_ROW_HEIGHT}
        alwaysVisible={selected}
      >
        <ElementListRow
          variant="group"
          depth={startDepth}
          groupIcon={categoryIcon}
          namePlaceholder={categoryName}
          quantityPlaceholder={quantityPlaceholder}
          quantityDisplayNumber={quantityDisplayNumber}
          unit={categoryGroup.unit}
          quantityDisplayUnit={displayUnit}
          quantityReadonly
          nameReadonly
          expandable={categoryGroup.elements.length > 0 && !forceExpanded}
          expanded={expanded}
          onExpandedChange={handleExpandedChange}
          selected={selected}
          onSelect={handleSelect}
          proposals={proposalBars}
          maxCo2={co2ePerGfaMax > 0 ? co2ePerGfaMax : undefined}
          maxCost={costPerGfaMax > 0 ? costPerGfaMax : undefined}
          hideDivider={hideDivider}
        />
      </HideOutsideViewport>
      {expanded &&
        categoryGroup.visibleElements.map((productGroup) => (
          <ProductGroupItemRow
            key={productGroup.id}
            productGroup={productGroup}
            depth={startDepth + 1}
            categoryIcon={categoryIcon}
            forceExpanded={forceExpanded}
          />
        ))}
    </>
  );
};

export default ProductCategoryGroupRow;
